// Detección real de capacidades: nunca se promete un códec que el navegador no puede decodificar.
import type { Media, VideoVariant } from "../types";

type MSLike = { isTypeSupported(type: string): boolean };

function mse(): MSLike | null {
  const w = window as unknown as { ManagedMediaSource?: MSLike; MediaSource?: MSLike; WebKitMediaSource?: MSLike };
  return w.MediaSource ?? w.ManagedMediaSource ?? w.WebKitMediaSource ?? null;
}

export function canPlayType(mime: string): boolean {
  const ms = mse();
  if (ms) return ms.isTypeSupported(mime);
  // iPhone antiguo sin MSE: reproducción HLS nativa.
  const v = document.createElement("video");
  return v.canPlayType(mime) === "probably";
}

export function videoSupported(v: VideoVariant): boolean {
  return canPlayType(`video/mp4; codecs="${v.codecs}"`);
}

export function audioSupported(codecs: string): boolean {
  return canPlayType(`audio/mp4; codecs="${codecs}"`);
}

export async function smoothDecode(v: VideoVariant): Promise<boolean> {
  const mc = (navigator as Navigator & { mediaCapabilities?: MediaCapabilities }).mediaCapabilities;
  if (!mc) return true;
  try {
    const info = await mc.decodingInfo({
      type: "media-source",
      video: {
        contentType: `video/mp4; codecs="${v.codecs}"`, width: v.width, height: v.height,
        bitrate: v.bandwidth || 5_000_000, framerate: v.fps || 24,
        ...(v.range === "PQ" ? { transferFunction: "pq" as const, colorGamut: "rec2020" as const } : {}),
        ...(v.range === "HLG" ? { transferFunction: "hlg" as const, colorGamut: "rec2020" as const } : {}),
      },
    });
    return info.supported && info.smooth;
  } catch {
    return true;
  }
}

const CODEC_NAMES: Record<string, string> = { av1: "AV1", hevc: "HEVC (H.265)", h264: "H.264", vp9: "VP9" };

export interface Compatibility {
  playable: VideoVariant[];
  unsupported: VideoVariant[];
  audioPlayable: number;
  message: string | null;
}

export function checkMedia(m: Media): Compatibility {
  const playable = m.video.filter(videoSupported);
  const unsupported = m.video.filter((v) => !playable.includes(v));
  const audioPlayable = m.audio.filter((a) => !a.codecs || audioSupported(a.codecs)).length;
  let message: string | null = null;
  if (!playable.length) {
    const codecs = [...new Set(m.video.map((v) => CODEC_NAMES[v.codec] ?? v.codec))].join(", ");
    message =
      `Este dispositivo no puede reproducir ${codecs}, y este título no tiene una versión compatible. ` +
      (m.video.some((v) => v.codec === "av1")
        ? "Prueba con Chrome, Edge o Firefox actualizados, o pide a quien administra Radiant que genere la variante H.264 de compatibilidad."
        : "Prueba con otro navegador o dispositivo.");
  } else if (m.audio.length && !audioPlayable) {
    message = "Este navegador no puede reproducir ninguna de las pistas de audio de este título (se verá sin sonido).";
  }
  return { playable, unsupported, audioPlayable, message };
}

export function capabilitySummary(): Record<string, boolean> {
  return {
    "AV1 10 bits": canPlayType('video/mp4; codecs="av01.0.08M.10"'),
    "HEVC": canPlayType('video/mp4; codecs="hvc1.1.6.L120.B0"'),
    "H.264": canPlayType('video/mp4; codecs="avc1.640028"'),
    "Opus": canPlayType('audio/mp4; codecs="opus"'),
    "AAC": canPlayType('audio/mp4; codecs="mp4a.40.2"'),
    "E-AC-3": canPlayType('audio/mp4; codecs="ec-3"'),
  };
}
